// Singleton that fetches the admin-uploaded default drum kit and pre-loads
// each slot's audio sample into a Tone.Player. The GenPlayer (landing page +
// Training Lab) and AudioEngine (DAW) consult this manager before falling
// back to their built-in synthesized drums.
import * as Tone from "tone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class DrumKitManager {
  constructor() {
    this.kit = null;           // { id, name, samples: {slot: {url, ...}} }
    this.players = {};         // slot -> Tone.Player (connected to destination)
    this.subscribers = new Set();
    this.loaded = false;
  }

  // Pre-fetch default kit metadata. Idempotent — safe to call multiple times.
  // `force=true` re-fetches even if we already have data (used after admin
  // changes the default kit and wants to hear the new one immediately).
  async refresh(force = false) {
    if (this.loaded && !force) return this.kit;
    try {
      const res = await fetch(`${BACKEND_URL}/api/training/kits/default`);
      const data = await res.json();
      const kit = data?.kit || null;
      // Dispose previous players to avoid leaks
      Object.values(this.players).forEach((p) => { try { p.dispose(); } catch { /* noop */ } });
      this.players = {};
      this.kit = kit;
      if (kit && kit.samples) {
        await Tone.start();
        const promises = Object.entries(kit.samples).map(async ([slot, meta]) => {
          if (!meta?.url) return;
          try {
            const player = new Tone.Player({ url: `${BACKEND_URL}${meta.url}`, autostart: false }).toDestination();
            await Tone.loaded();
            this.players[slot] = player;
          } catch (e) {
            console.warn(`DrumKit: failed to load ${slot}`, e);
          }
        });
        await Promise.all(promises);
      }
      this.loaded = true;
      this.subscribers.forEach((fn) => { try { fn(this.kit); } catch { /* noop */ } });
      return this.kit;
    } catch (e) {
      console.warn("DrumKit: refresh failed", e);
      return null;
    }
  }

  hasSample(slot) {
    return !!this.players[slot];
  }

  // Returns { slot: absoluteUrl } for every slot that has an uploaded sample.
  // Used by offline exporters that must instantiate their own Tone.Player
  // instances INSIDE the Tone.Offline context (the live players are bound
  // to the online audio context and can't be reused for offline rendering).
  getSampleUrls() {
    const out = {};
    const samples = this.kit?.samples || {};
    for (const [slot, meta] of Object.entries(samples)) {
      if (meta?.url) out[slot] = `${BACKEND_URL}${meta.url}`;
    }
    return out;
  }

  // Trigger a slot's sample at the given audio-context time. Returns true if
  // a sample fired so the caller can skip the synthesized fallback.
  trigger(slot, time, velocity = 1) {
    const p = this.players[slot];
    if (!p || !p.loaded) return false;
    try {
      // Clone-start so overlapping hits don't cut each other off
      p.volume.value = velocity >= 1 ? 0 : (20 * Math.log10(Math.max(0.001, velocity)));
      p.start(time);
      return true;
    } catch {
      return false;
    }
  }

  getKit() { return this.kit; }
  subscribe(fn) { this.subscribers.add(fn); return () => this.subscribers.delete(fn); }
}

const drumKitManager = new DrumKitManager();
export default drumKitManager;
